<template>
  <div :class="$style.container">
    <h2 :class="$style.container__header">選択中の日程</h2>
    <div :class="$style.container__items">
      <TimeSlotCard
        v-for="ts in modelValue"
        :key="`${ts.startsAt}::${ts.endsAt}`"
        :timeSlot="ts"
        variant="rich"
        @onCloseClicked="$emit('update:modelValue', togglePicked(ts))"
      />
    </div>
    <label :class="$style.fallback">
      <div :class="$style.fallback__label">
        <span>外部カレンダー URL（任意）</span>
      </div>
      <DolphinTextInput
        :class="$style.fallback__input"
        v-model="fallbackUrl"
        :error="fallbackUrlError"
      />
      <div :class="$style.fallback__error" v-if="fallbackUrlError">
        {{ fallbackUrlError }}
      </div>

      <div :class="$style.fallback__note">
        <span>
          ここに URL
          を入力すると、「別の日程を選ぶ」がオカネコカレンダーではなく、入力された
          URL が開きます
        </span>
      </div>
    </label>
    <FlatButton
      :class="$style.container__button"
      @click="$emit('onDone')"
      :disabled="!!fallbackUrlError"
      >決定
    </FlatButton>
  </div>
</template>

<script setup lang="ts">
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import TimeSlotCard from '@/components/organisms/TimeSlotPickerModal/TimeSlotCard.vue'
import { computed, toRefs } from 'vue'
import { type PickedTimeSlots, type TimeSlot, useTimeSlot } from './useTimeSlot'
const props = defineProps<{
  fallbackUrl?: Maybe<string>
  modelValue: PickedTimeSlots
}>()
const { modelValue } = toRefs(props)

const emit = defineEmits<{
  (e: 'update:modelValue', value: TimeSlot[]): void
  (e: 'update:fallbackUrl', value: Maybe<string>): void
  (e: 'onDone'): void
}>()

const fallbackUrl = computed({
  get(): Maybe<string> {
    return props.fallbackUrl
  },
  set(v: Maybe<string>) {
    emit('update:fallbackUrl', v)
  },
})

const fallbackUrlError = computed(() => {
  if (fallbackUrl.value) {
    try {
      new URL(fallbackUrl.value)
      return null
    } catch {
      return '有効なURLを入力してください'
    }
  }
  return null
})

const { togglePicked } = useTimeSlot(modelValue)
</script>

<style module lang="scss">
.container {
  padding: 8px;
  flex-direction: column;
  display: flex;

  &__header {
    padding: 4px;
    font-weight: 700;
    font-size: 14px;
    text-align: center;
  }

  &__items {
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex: 1;
  }

  &__button {
    margin-top: 20px;
  }
}

.fallback {
  padding: 0 8px;

  &__input {
    width: 100%;
    margin-bottom: 4px;
  }

  &__label {
    padding: 4px;
    margin-bottom: 4px;
    font-weight: 700;
    font-size: 14px;
    text-align: center;
  }

  &__note {
    font-size: 10px;
    color: $color-text-and-object-low-emphasis;

    &::before {
      content: '*';
      font-size: 10px;
      margin-right: 0.5em;
    }
  }

  &__error {
    margin-top: 4px;
    font-size: 12px;
    color: $color-caution-accent;
  }
}
</style>
